import React, { useCallback, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useIsTransactionJournalEntry } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerHooks";
import { Button } from "@design-system/Button/Button";
import { DisabledWithTooltip } from "@design-system/DisabledWithTooltip/DisabledWithTooltip";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./JournalEntryDrawer.module.scss";
import {
    JournalEntryDrawerErrors,
    JournalEntryDrawerFormikConfig,
} from "./JournalEntryDrawer";
import { JournalEntryDrawerMutableConfig } from "./JournalEntryDrawer.lib";
import {
    buildEmptyLines,
    calculateDebitAndCreditTotals,
} from "./JournalEntryDrawerUtils";
import { JournalEntryPersonalTransactionMessage } from "./JournalEntryPersonalTransactionMessage";

interface Props {
    loading: boolean;
    onDelete: () => void;
    onDuplicate?: () => void;
    isLocked: boolean;
    visible: boolean;
}

export const JournalEntryDrawerFooter: React.FC<Props> = ({
    loading,
    onDelete,
    onDuplicate,
    isLocked,
    visible,
}) => {
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, dirty, setFieldValue, handleSubmit } = formikContext;
    const hideAfterSubmit = values.isHideAfterSubmit;
    const errors = formikContext.errors as JournalEntryDrawerErrors;
    const isReversedJournalEntry =
        !!values.journalEntry.reversedManualJournalEntryId;
    const isTransactionJournal = useIsTransactionJournalEntry(values);

    const lines = values.journalEntry.lines;
    const linesRef = useRef(lines);
    linesRef.current = lines;

    const handleAddRows = useCallback(() => {
        void setFieldValue("journalEntry.lines", [
            ...linesRef.current,
            ...buildEmptyLines(20),
        ]);
    }, [setFieldValue]);

    const debitAndCreditTotals = useMemo(
        () => calculateDebitAndCreditTotals(lines),
        [lines],
    );

    const isFormNotFilled = useMemo(
        () =>
            debitAndCreditTotals.debitAmount === 0 &&
            debitAndCreditTotals.creditAmount === 0,
        [debitAndCreditTotals],
    );

    const onSubmit = useCallback(
        (closeType: "save" | "save-and-new") => () => {
            if (closeType === "save") {
                setFieldValue("isHideAfterSubmit", true);
            } else {
                setFieldValue("isHideAfterSubmit", false);
            }
            JournalEntryDrawerMutableConfig.validate = true;
            handleSubmit();
        },
        [handleSubmit, setFieldValue],
    );

    const shownError = useMemo(() => {
        const hasErrorsInLines =
            errors.journalEntry?.lines && errors.journalEntry.lines.length > 0;
        const hasErrorsInDebitAndCreditSums = errors.debitAndCreditSums;
        if (hasErrorsInLines && hasErrorsInDebitAndCreditSums) {
            return "Please complete the highlighted fields and make sure the totals match";
        }
        if (hasErrorsInLines) {
            return "Please complete the highlighted fields";
        }
        if (hasErrorsInDebitAndCreditSums) {
            return "Please check that your total debits match total credits";
        }
        return "";
    }, [errors]);

    return (
        <div className={styles.modalFooter}>
            {visible && (
                <div className={styles.footerButtons}>
                    <div>
                        {!isLocked &&
                            !isTransactionJournal &&
                            !isReversedJournalEntry && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    onClick={handleAddRows}
                                >
                                    + 20 rows
                                </Button>
                            )}
                        {!isLocked &&
                            values.journalEntry.id &&
                            !isTransactionJournal && (
                                <ButtonWithLoader
                                    loading={loading && hideAfterSubmit}
                                    disabled={
                                        isFormNotFilled || shownError.length > 0
                                    }
                                    variant="primary"
                                    size="md"
                                    onClick={onDelete}
                                    className={styles.deleteButton}
                                >
                                    Delete journal entry
                                </ButtonWithLoader>
                            )}
                        <JournalEntryPersonalTransactionMessage />
                    </div>

                    <HStack align="center" gap={12}>
                        {shownError && (
                            <p className={styles.footerError}>{shownError}</p>
                        )}
                        {onDuplicate && (
                            <DisabledWithTooltip
                                reason={
                                    dirty
                                        ? "Save your changes before duplicating"
                                        : null
                                }
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    disabled={loading || dirty}
                                    onClick={onDuplicate}
                                >
                                    Duplicate
                                </Button>
                            </DisabledWithTooltip>
                        )}
                        {!isLocked && (
                            <>
                                <ButtonWithLoader
                                    loading={loading && !hideAfterSubmit}
                                    disabled={
                                        isFormNotFilled ||
                                        shownError.length > 0 ||
                                        isReversedJournalEntry ||
                                        isTransactionJournal
                                    }
                                    onClick={onSubmit("save-and-new")}
                                    variant="primary"
                                    size="md"
                                >
                                    Save and new
                                </ButtonWithLoader>
                                <ButtonWithLoader
                                    loading={loading && hideAfterSubmit}
                                    disabled={
                                        isFormNotFilled ||
                                        shownError.length > 0 ||
                                        isReversedJournalEntry
                                    }
                                    variant="primary"
                                    size="md"
                                    onClick={onSubmit("save")}
                                    hotKey="mod+enter"
                                    hotKeyDisplayPosition="inline"
                                    hotKeyOptions={{
                                        description: "Save",
                                    }}
                                >
                                    Save
                                </ButtonWithLoader>
                            </>
                        )}
                    </HStack>
                </div>
            )}
        </div>
    );
};
